import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {IStore} from '../../redux/interface';
import {
  getDashboardStatistics,
  statisticsDashboardAsyncActions
} from '../../redux/pageAsyncActions';
import StatisticsIcon from '../../assets/image/menu-auth/pieChart.png';
import Loader from '../../components/loader';
import {StatisticsPieChart} from '../../components/statistics-pie-chart/StatisticsPieChart';
import InputRadio from '../../components/input-radio';
import {IRadioInput} from '../../components/input-radio/interfaces';
import InputDate from '../../components/input-date';
import NotFoundInfo from '../../components/not-found-info';
import CrossroadImg from '../../assets/image/common/notFound.png';
import {useIntl} from 'react-intl';
import StatisticsBarChart from '../../components/statistics-bar-chart';

import './styles.scss';

type DateFilter = 'ALL' | 'TODAY' | 'YESTERDAY' | 'LAST_WEEK' | 'LAST_MONTH';

const fmtInput = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
  d.getDate()
).padStart(2, '0')}`;

const startOfDay = (d: Date) => {
  const s = new Date(d);

  s.setHours(0, 0, 0, 0);

  return s;
};

const presetRange = (
  preset: DateFilter,
  now: Date = new Date()
): {from: Date; to: Date} | null => {
  const todayStart = startOfDay(now);
  const daysAgo = (n: number) => {
    const d = new Date(todayStart);

    d.setDate(d.getDate() - n);

    return d;
  };
  const daysAfter = (n: number) => {
    const d = new Date(todayStart);

    d.setDate(d.getDate() + n);

    return d;
  };

  const map: Record<DateFilter, {from: Date; to: Date} | null> = {
    ALL: null,
    TODAY: {from: todayStart, to: daysAfter(1)},
    YESTERDAY: {from: startOfDay(daysAgo(1)), to: todayStart},
    LAST_WEEK: {from: startOfDay(daysAgo(7)), to: daysAfter(1)},
    LAST_MONTH: {from: startOfDay(daysAgo(30)), to: daysAfter(1)}
  };

  return map[preset];
};

const DatePresetRadios: React.FC<{
  value: DateFilter;
  onChange: (v: DateFilter) => void;
}> = ({value, onChange}) => {
  const intl = useIntl();
  const options: IRadioInput[] = [
    {
      id: 1,
      name: 'All',
      label: intl.formatMessage({
        id: 'gritx.statistics.filter.dates.all',
        defaultMessage: 'All'
      }),
      value: 'ALL'
    },
    {
      id: 2,
      name: 'Today',
      label: intl.formatMessage({
        id: 'gritx.statistics.filter.dates.today',
        defaultMessage: 'Today'
      }),
      value: 'TODAY'
    },
    {
      id: 3,
      name: 'Yesterday',
      label: intl.formatMessage({
        id: 'gritx.statistics.filter.dates.yesterday',
        defaultMessage: 'Yesterday'
      }),
      value: 'YESTERDAY'
    },
    {
      id: 4,
      name: 'Last week',
      label: intl.formatMessage({
        id: 'gritx.statistics.filter.dates.lastWeek',
        defaultMessage: 'Last week'
      }),
      value: 'LAST_WEEK'
    },
    {
      id: 5,
      name: 'Last month',
      label: intl.formatMessage({
        id: 'gritx.statistics.filter.dates.lastMonth',
        defaultMessage: 'Last week'
      }),
      value: 'LAST_MONTH'
    }
  ];

  const handleChangeFilter = (val: string)=> {
    onChange(val as DateFilter);
  };

  return (
    <div className="dashboard__filters-radios" role="radiogroup" aria-label="Date presets">
      <InputRadio inputs={options} value={value} onChange={handleChangeFilter} label=""/>
    </div>
  );
};

const DateRangeInputs: React.FC<{
  from: Date | undefined;
  to: Date | undefined;
  setFrom: (v: Date) => void;
  setTo: (v: Date) => void;
  touched: {from?: boolean; to?: boolean};
  setTouched: React.Dispatch<React.SetStateAction<{from?: boolean; to?: boolean}>>;
}> = ({from, to, setFrom, setTo, touched, setTouched}) => {
  return (
    <div className="dashboard__filters-dates">
      <div className="date-field">
        <InputDate
          aria-label="From date"
          value={from}
          onChange={(date) => setFrom(date)}
          onBlur={() => setTouched((t) => ({...t, from: true}))}
        />
      </div>

      <div className="date-field">
        <InputDate
          aria-label="To date"
          value={to}
          onChange={(date) => setTo(date)}
          onBlur={() => setTouched((t) => ({...t, to: true}))}
        />
      </div>
    </div>
  );
};

export const StatisticsDashboard: React.FunctionComponent = () => {
  const {
    userProfile
  } = useSelector((state: IStore) => state.auth);
  const {
    stats
  } = useSelector((state: IStore) => state.statistics_dashboard);
  const {
    loading
  } = useSelector((state: IStore) => state.status);
  const dispatch = useDispatch();
  const intl = useIntl();

  const [dateFilter, setDateFilter] = useState<DateFilter>('ALL');
  const [fromDate, setFromDate] = useState<Date>();
  const [toDate, setToDate] = useState<Date>();
  const [touched, setTouched] = useState<{from?: boolean; to?: boolean}>({});

  useEffect(() => {
    const r = presetRange(dateFilter);

    if (r) {
      setFromDate(r.from);
      setToDate(r.to);
    } else {
      setFromDate(undefined);
      setToDate(undefined);
    }
    setTouched({});
  }, [dateFilter]);

  useEffect(() => {
    if (!userProfile || !userProfile.school) {
      return;
    }
    dispatch(statisticsDashboardAsyncActions[getDashboardStatistics](userProfile.userGroupId,
      fromDate ? fmtInput(fromDate) : undefined,
      toDate ? fmtInput(toDate) : undefined));
  }, [dispatch, fromDate, toDate, userProfile]);

  return (
    <div className="dashboard">
      {userProfile && userProfile.school && <h1 className="dashboard__title">
        <img src={StatisticsIcon} alt="" className="dashboard__icon"/>
        {userProfile && userProfile.school}
      </h1>}
      {userProfile && !userProfile.school && <NotFoundInfo
        title={intl.formatMessage({
          id: 'gritx.statistics.noContent',
          defaultMessage: 'Sorry, statistics are not available.'
        })}
        image={CrossroadImg}/>}

      {stats && <div className="dashboard__filters" aria-label="Filter by date">
        <DatePresetRadios value={dateFilter} onChange={setDateFilter} />
        <DateRangeInputs
          from={fromDate}
          to={toDate}
          setFrom={setFromDate}
          setTo={setToDate}
          touched={touched}
          setTouched={setTouched}
        />
      </div>}
      {loading.includes(getDashboardStatistics) ? <Loader className="dashboard__loader"/> : <></>}
      <div className="dashboard__wrapper">
        {stats?.stats && <section className="dashboard__cards" aria-label="Summary metrics">
          {stats.stats.map((s) => (
            <div key={s.name} className="dashboard__card">
              <div className="dashboard__card-label">{s.name}</div>
              <div className="dashboard__card-value">{s.value}</div>
            </div>
          ))}
        </section>}

        <section className="dashboard__charts" aria-label="Charts">
          {stats?.challengesChart && <div className="dashboard__charts-panel">
            <header className="dashboard__charts-panel-header">
              <h3>Challenges in Dialogues</h3>
            </header>
            <div className="dashboard__charts-panel-body">
              <div id="challenges-chart" className="chart-placeholder" aria-label="Challenges chart">
                <StatisticsPieChart chart={stats.challengesChart}/>
              </div>
            </div>
          </div>}

          {stats?.emotionsChart && <div className="dashboard__charts-panel">
            <header className="dashboard__charts-panel-header">
              <h3>Emotions in Dialogues</h3>
            </header>
            <div className="dashboard__charts-panel-body">
              <StatisticsPieChart chart={stats.emotionsChart}/>
              <div id="emotions-chart" className="chart-placeholder" aria-label="Emotions chart"/>
            </div>
          </div>}

          {stats?.journalsChart && <div className="dashboard__charts-panel">
            <header className="dashboard__charts-panel-header">
              <h3>Records</h3>
            </header>
            <div className="dashboard__charts-panel-body">
              <div id="journals-chart" className="chart-placeholder" aria-label="Records chart">
                <StatisticsBarChart chart={stats.journalsChart}/>
              </div>
            </div>
          </div>}
        </section>

      </div>
    </div>
  );
};
